import React, { useMemo } from "react";

import { Theme, useTheme } from "@mui/material/styles";

import {
  FullWidthFieldContainer,
  RowContainer,
  ShortFieldContainer,
  SingleItemRowContainer,
  abbreviate,
  formatDate,
  logDebug,
  mergeProperties,
} from "packable-components";
import { OrderItemProps, ShipmentType, ShippingStatus } from "./Order.types";
import { Divider, Grid, Typography } from "@mui/material";
import OrderStatus from "./OrderStatus";
import OrderRequestedActions from "./OrderRequestedActions";
import OrderAcceptedActions from "./OrderAcceptedActions";
import OrderLabelsShippedActions from "./OrderLabelsShippedActions";
import OrderUserCancelAction from "./OrderUserCancelAction";
import OrderCancellationRequestedActions from "./OrderCancellationRequestedActions";
import OrderUserSchedulePickupAction from "./OrderUserSchedulePickupAction";
import OrderPickupRequestedActions from "./OrderPickupRequestedActions";
import OrderPickupScheduledActions from "./OrderPickupScheduledActions";
import OrderPickupCompletedActions from "./OrderPickupCompletedActions";
import OrderReturnRequestedActions from "./OrderReturnRequestedActions";

const getStyles = (theme: Theme) => ({
  OrderItemContainer: {
    width: "512px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignContent: "center",
    alignItems: "center",
  },
  OrderItemContainerDivider: {
    width: "100%",
    mb: 1,
    mt: 1,
  },
  OrderItemContentShortTextField: {
    width: "240px",
  },
  OrderItemContentFullWidthTextField: {
    width: "100%",
  },
  OrderItemContentActionRow: {
    padding: 1,
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-end",
  },
  OrderItemContentActionRemoveButton: {
    muiVariant: "contained",
    muiColor: "secondary",
  },
});

export const OrderItem: React.FC<OrderItemProps> = (
  props
): React.JSX.Element => {
  const {
    orderItem,
    orderItemAction,
    onOrderItemActionUpdate,
    multipleItems = false,
    isAdmin,
    dimensionUnit,
    weigthUnit,
    styles,
  } = props;

  logDebug("OrderItem PROPS");
  logDebug(props);

  const theme = useTheme();

  const defaultStyles = useMemo(() => getStyles(theme), [theme]);
  const effectiveStyles = useMemo(
    () =>
      styles === undefined
        ? defaultStyles
        : mergeProperties(defaultStyles, styles),
    [defaultStyles, styles]
  );

  logDebug("OrderItem effectiveStyles");
  logDebug(effectiveStyles);

  const getLabel = (): string => {
    if (
      orderItem?.shipmentItem !== null &&
      orderItem?.shipmentItem.type === ShipmentType.STORAGE
    ) {
      return `${orderItem?.itemLabel} - store until ${formatDate(
        new Date(orderItem?.storageEndDate).toDateString() || "",
        Intl.DateTimeFormat().resolvedOptions().locale,
        { dateStyle: "short" }
      )}`;
    }
    return orderItem?.itemLabel || "";
  };

  return (
    <>
      {orderItem ? (
        <Grid
          className="OrderItemContainer"
          sx={effectiveStyles.OrderItemContainer}
        >
          <Divider
            className="OrderItemContainerDivider"
            sx={effectiveStyles.OrderItemContainerDivider}
          />
          <RowContainer>
            <ShortFieldContainer>
              <Grid
                className="OrderItemContentShortTextField"
                sx={effectiveStyles.OrderItemContentShortTextField}
              >
                <Typography variant="body1">{getLabel()}</Typography>
              </Grid>
            </ShortFieldContainer>
            <ShortFieldContainer>
              <Grid
                className="OrderItemContentShortTextField"
                sx={effectiveStyles.OrderItemContentShortTextField}
              >
                {orderItem.shipmentItem !== null && (
                  <OrderStatus
                    status={orderItem.shipmentItem.status}
                    isAdmin={isAdmin}
                    displayChipForZeroCount
                  />
                )}
              </Grid>
            </ShortFieldContainer>
          </RowContainer>
          {orderItem.shipmentItem !== null &&
            orderItem.shipmentItem.trackingNumber !== undefined &&
            orderItem.shipmentItem.trackingNumber !== null &&
            orderItem.shipmentItem.trackingNumber.length > 0 && (
              <SingleItemRowContainer>
                <FullWidthFieldContainer>
                  <Grid
                    className="OrderItemContentFullWidthTextField"
                    sx={effectiveStyles.OrderItemContentFullWidthTextField}
                  >
                    <Typography variant="body2">
                      {`Shipping Tracking: ${orderItem.shipmentItem.trackingNumber}`}
                    </Typography>
                  </Grid>
                </FullWidthFieldContainer>
              </SingleItemRowContainer>
            )}
          {orderItem.storageItem !== null &&
            orderItem.storageItem.trackingNumber !== undefined &&
            orderItem.storageItem.trackingNumber !== null &&
            orderItem.storageItem.trackingNumber.length > 0 && (
              <SingleItemRowContainer>
                <FullWidthFieldContainer>
                  <Grid
                    className="OrderItemContentFullWidthTextField"
                    sx={effectiveStyles.OrderItemContentFullWidthTextField}
                  >
                    <Typography variant="body2">
                      {`Storage Tracking: ${orderItem.storageItem.trackingNumber}`}
                    </Typography>
                  </Grid>
                </FullWidthFieldContainer>
              </SingleItemRowContainer>
            )}
          <RowContainer>
            <ShortFieldContainer>
              <Grid
                className="OrderItemContentShortTextField"
                sx={effectiveStyles.OrderItemContentShortTextField}
              >
                <Typography variant="body2">
                  {`Dimensions: ${orderItem?.itemLength} x ${
                    orderItem?.itemWidth
                  } x ${orderItem?.itemHeight} ${abbreviate(dimensionUnit)}`}
                </Typography>
              </Grid>
            </ShortFieldContainer>
            <ShortFieldContainer>
              <Grid
                className="OrderItemContentShortTextField"
                sx={effectiveStyles.OrderItemContentShortTextField}
              >
                <Typography variant="body2">
                  {`Weight: ${orderItem?.itemWeight} ${abbreviate(weigthUnit)}`}
                </Typography>
              </Grid>
            </ShortFieldContainer>
          </RowContainer>
          {orderItem &&
            orderItem.shipmentItem !== null &&
            orderItem.shipmentItem.status === ShippingStatus.DELIVERED &&
            orderItem.shipmentItem.actualDelivery && (
              <SingleItemRowContainer>
                <FullWidthFieldContainer>
                  <Grid
                    className="OrderItemContentFullWidthTextField"
                    sx={effectiveStyles.OrderItemContentFullWidthTextField}
                  >
                    <Typography sx={{ fontWeight: 700 }} variant="body2">
                      {`Delivered on: ${new Intl.DateTimeFormat(
                        Intl.DateTimeFormat().resolvedOptions().locale,
                        { dateStyle: "short", timeStyle: "short" }
                      ).format(
                        new Date(orderItem.shipmentItem.actualDelivery)
                      )}`}
                    </Typography>
                  </Grid>
                </FullWidthFieldContainer>
              </SingleItemRowContainer>
            )}
          {orderItem &&
            orderItem.shipmentItem !== null &&
            (orderItem.shipmentItem.status ===
              ShippingStatus.PICKUP_REQUESTED ||
              orderItem.shipmentItem.status ===
                ShippingStatus.PICKUP_SCHEDULED ||
              orderItem.shipmentItem.status ===
                ShippingStatus.PICKUP_COMPLETED) &&
            orderItem.shipmentItem.requestedPickup && (
              <SingleItemRowContainer>
                <FullWidthFieldContainer>
                  <Grid
                    className="OrderItemContentFullWidthTextField"
                    sx={effectiveStyles.OrderItemContentFullWidthTextField}
                  >
                    {orderItem.shipmentItem.usePickupHour ? (
                      <Typography sx={{ fontWeight: 700 }} variant="body2">
                        {`Pickup Requested for: ${new Intl.DateTimeFormat(
                          Intl.DateTimeFormat().resolvedOptions().locale,
                          { dateStyle: "short", timeStyle: "short" }
                        ).format(
                          new Date(orderItem.shipmentItem.requestedPickup)
                        )}`}
                      </Typography>
                    ) : (
                      <Typography sx={{ fontWeight: 700 }} variant="body2">
                        {`Pickup Requested for: ${new Intl.DateTimeFormat(
                          Intl.DateTimeFormat().resolvedOptions().locale,
                          { dateStyle: "short" }
                        ).format(
                          new Date(orderItem.shipmentItem.requestedPickup)
                        )}`}
                      </Typography>
                    )}
                  </Grid>
                </FullWidthFieldContainer>
              </SingleItemRowContainer>
            )}
          {orderItem &&
            orderItem.shipmentItem !== null &&
            (orderItem.shipmentItem.status ===
              ShippingStatus.PICKUP_SCHEDULED ||
              orderItem.shipmentItem.status ===
                ShippingStatus.PICKUP_COMPLETED) &&
            orderItem.shipmentItem.scheduledPickup && (
              <SingleItemRowContainer>
                <FullWidthFieldContainer>
                  <Grid
                    className="OrderItemContentFullWidthTextField"
                    sx={effectiveStyles.OrderItemContentFullWidthTextField}
                  >
                    {orderItem.shipmentItem.usePickupHour ? (
                      <Typography sx={{ fontWeight: 700 }} variant="body2">
                        {`Pickup Scheduled for: ${new Intl.DateTimeFormat(
                          Intl.DateTimeFormat().resolvedOptions().locale,
                          { dateStyle: "short", timeStyle: "short" }
                        ).format(
                          new Date(orderItem.shipmentItem.scheduledPickup)
                        )}`}
                      </Typography>
                    ) : (
                      <Typography sx={{ fontWeight: 700 }} variant="body2">
                        {`Pickup Scheduled for: ${new Intl.DateTimeFormat(
                          Intl.DateTimeFormat().resolvedOptions().locale,
                          { dateStyle: "short" }
                        ).format(
                          new Date(orderItem.shipmentItem.scheduledPickup)
                        )}`}
                      </Typography>
                    )}
                  </Grid>
                </FullWidthFieldContainer>
              </SingleItemRowContainer>
            )}
          {orderItem &&
            orderItem.shipmentItem !== null &&
            orderItem.shipmentItem.status === ShippingStatus.PICKUP_COMPLETED &&
            orderItem.shipmentItem.actualPickup && (
              <SingleItemRowContainer>
                <FullWidthFieldContainer>
                  <Grid
                    className="OrderItemContentFullWidthTextField"
                    sx={effectiveStyles.OrderItemContentFullWidthTextField}
                  >
                    <Typography sx={{ fontWeight: 700 }} variant="body2">
                      {`Pickup Completed at: ${new Intl.DateTimeFormat(
                        Intl.DateTimeFormat().resolvedOptions().locale,
                        { dateStyle: "short", timeStyle: "short" }
                      ).format(new Date(orderItem.shipmentItem.actualPickup))}`}
                    </Typography>
                  </Grid>
                </FullWidthFieldContainer>
              </SingleItemRowContainer>
            )}
          {orderItem &&
            orderItem.shipmentItem !== null &&
            orderItem.shipmentItem.notes !== undefined &&
            orderItem.shipmentItem.notes !== null &&
            orderItem.shipmentItem.notes.length > 0 && (
              <SingleItemRowContainer>
                <FullWidthFieldContainer>
                  <Grid
                    className="OrderItemContentFullWidthTextField"
                    sx={effectiveStyles.OrderItemContentFullWidthTextField}
                  >
                    <Typography variant="body2">
                      {`Notes: ${orderItem.shipmentItem.notes}`}
                    </Typography>
                  </Grid>
                </FullWidthFieldContainer>
              </SingleItemRowContainer>
            )}
          {orderItem && orderItem.shipmentItem !== null && (
            <RowContainer>
              {orderItem.shipmentItem.status === ShippingStatus.REQUESTED &&
                orderItem.shipmentItem.type === ShipmentType.STORAGE &&
                isAdmin && (
                  <OrderRequestedActions
                    orderItemAction={orderItemAction}
                    onOrderItemActionUpdate={onOrderItemActionUpdate}
                  />
                )}
              {orderItem.shipmentItem.status === ShippingStatus.REQUESTED &&
                orderItem.shipmentItem.type === ShipmentType.HOME &&
                isAdmin && (
                  <OrderReturnRequestedActions
                    orderItemAction={orderItemAction}
                    onOrderItemActionUpdate={onOrderItemActionUpdate}
                    multipleItems={multipleItems}
                  />
                )}
              {orderItem.shipmentItem.status ===
                ShippingStatus.CANCEL_REQUESTED &&
                isAdmin && (
                  <OrderCancellationRequestedActions
                    orderItemAction={orderItemAction}
                    onOrderItemActionUpdate={onOrderItemActionUpdate}
                  />
                )}
              {orderItem.shipmentItem.status === ShippingStatus.ACCEPTED &&
                isAdmin && (
                  <OrderAcceptedActions
                    orderItemAction={orderItemAction}
                    onOrderItemActionUpdate={onOrderItemActionUpdate}
                  />
                )}
              {orderItem.shipmentItem.status ===
                ShippingStatus.LABELS_SHIPPED &&
                isAdmin && (
                  <OrderLabelsShippedActions
                    orderItemAction={orderItemAction}
                    onOrderItemActionUpdate={onOrderItemActionUpdate}
                  />
                )}
              {orderItem.shipmentItem.status ===
                ShippingStatus.PICKUP_REQUESTED &&
                isAdmin && (
                  <OrderPickupRequestedActions
                    orderItemAction={orderItemAction}
                    onOrderItemActionUpdate={onOrderItemActionUpdate}
                  />
                )}
              {orderItem.shipmentItem.status ===
                ShippingStatus.PICKUP_SCHEDULED &&
                isAdmin && (
                  <OrderPickupScheduledActions
                    orderItemAction={orderItemAction}
                    onOrderItemActionUpdate={onOrderItemActionUpdate}
                  />
                )}
              {orderItem.shipmentItem.status ===
                ShippingStatus.PICKUP_COMPLETED &&
                isAdmin && (
                  <OrderPickupCompletedActions
                    shipmentType={orderItem.shipmentItem.type}
                    orderItemAction={orderItemAction}
                    onOrderItemActionUpdate={onOrderItemActionUpdate}
                  />
                )}
              {(orderItem.shipmentItem.status === ShippingStatus.DRAFT ||
                orderItem.shipmentItem.status === ShippingStatus.REQUESTED ||
                orderItem.shipmentItem.status === ShippingStatus.ACCEPTED) &&
                orderItem.shipmentItem.type === ShipmentType.STORAGE &&
                !isAdmin && (
                  <OrderUserCancelAction
                    originalStatus={orderItem.shipmentItem.status}
                    itemLabel={orderItem.itemLabel}
                    orderItemAction={orderItemAction}
                    onOrderItemActionUpdate={onOrderItemActionUpdate}
                  />
                )}
              {orderItem.shipmentItem.status ===
                ShippingStatus.LABELS_DELIVERED &&
                !isAdmin && (
                  <>
                    <OrderUserSchedulePickupAction
                      originalStatus={orderItem.shipmentItem.status}
                      itemLabel={orderItem.itemLabel}
                      orderItemAction={orderItemAction}
                      onOrderItemActionUpdate={onOrderItemActionUpdate}
                      multipleItems={multipleItems}
                    />
                    <OrderUserCancelAction
                      originalStatus={orderItem.shipmentItem.status}
                      itemLabel={orderItem.itemLabel}
                      orderItemAction={orderItemAction}
                      onOrderItemActionUpdate={onOrderItemActionUpdate}
                      disabled={
                        orderItemAction.shippingStatus ===
                        ShippingStatus.PICKUP_REQUESTED
                      }
                    />
                  </>
                )}
            </RowContainer>
          )}
        </Grid>
      ) : (
        <></>
      )}
    </>
  );
};

export default OrderItem;
